import { type StreamLanguage, type StreamParser } from '@codemirror/language';
import { type tags } from '@lezer/highlight';

import { api, isDefined } from 'common';

import { type AutocompleteLabels } from './types';

/**
 * Modifiers can follow standard tags. They are specifically defined in CodeMirror's highlight module.
 * https://lezer.codemirror.net/docs/ref/#highlight.tags.operator
 * We are using `special` modifier to indicate double braces.
 * `brace.special` can still use base `brace` highlighting. Using `special` makes it easier to debug tests.
 */
type TagTypes = keyof typeof tags | 'brace.special';

interface NodeNameTokenizerProps {
  allowedVariableNames: string[];
  flowParameterNames: string[];
  isJavascriptAllowed?: boolean;
  additionalAutoCompleteLabels?: AutocompleteLabels;
}

interface NodeNameTokenizerState {
  seenFirstDot: boolean;
  seenMoreDots: boolean;
  seenVariableName: boolean;
  seenValidVariableReference: boolean;
  seenInputsName: boolean;
  seenInputParameter: boolean;
  seenAutocompleteVariable: boolean;
  isInBlockComment: boolean;
  wholeWords: string[];
}

export function getNodeNameTokenizer(
  props: NodeNameTokenizerProps
): StreamParser<NodeNameTokenizerState> {
  const {
    allowedVariableNames,
    flowParameterNames,
    isJavascriptAllowed = false,
    additionalAutoCompleteLabels,
  } = props;

  const getStartState = (): NodeNameTokenizerState => {
    return {
      seenFirstDot: false,
      seenMoreDots: false,
      seenVariableName: false,
      seenValidVariableReference: false,
      seenInputsName: false,
      seenInputParameter: false,
      seenAutocompleteVariable: false,
      isInBlockComment: false,
      wholeWords: [],
    };
  };

  return {
    startState: getStartState,
    token(stream, state): TagTypes | null {
      if (isJavascriptAllowed && state.isInBlockComment) {
        while (!stream.eol()) {
          if (stream.match('*/')) {
            Object.assign(state, getStartState());
            return 'blockComment';
          }
          stream.next();
        }
        return 'blockComment';
      }
      if (stream.eatSpace()) {
        Object.assign(state, getStartState());
        return null;
      }
      if (isJavascriptAllowed && stream.match('/*')) {
        state.isInBlockComment = true;
        while (!stream.eol()) {
          if (stream.match('*/')) {
            Object.assign(state, getStartState());
            return 'blockComment';
          }
          stream.next();
        }
        return 'blockComment';
      }
      if (isJavascriptAllowed && stream.match('//')) {
        stream.skipToEnd();
        Object.assign(state, getStartState());
        return 'lineComment';
      }
      if (stream.match('`')) {
        while (!stream.eol() && !stream.match('`', false)) {
          stream.next();
        }
        stream.eat('`');
        return 'string';
      }
      // Handle square bracket notation for array/key access (e.g., [0], ['key'])
      if (stream.peek() === '[') {
        if (state.seenValidVariableReference || state.seenInputParameter || state.seenMoreDots) {
          stream.next(); // consume [
          // numeric index: [0], [123]
          if (stream.eatWhile(/\d/)) {
            if (stream.eat(']')) {
              return 'bracket';
            }
          }
          // quoted string key: ['key'] or ["key"]
          const quoteChar = stream.peek();
          if (quoteChar === "'" || quoteChar === '"') {
            stream.next(); // consume opening quote
            while (!stream.eol() && stream.peek() !== quoteChar) {
              stream.next();
            }
            if (stream.eat(quoteChar)) {
              if (stream.eat(']')) {
                return 'bracket';
              }
            }
          }
          return 'invalid';
        }
      }
      if (stream.eatWhile(/[()]/)) {
        Object.assign(state, getStartState());
        return 'bracket';
      }
      if (stream.eatWhile(/[;/]/)) {
        Object.assign(state, getStartState());
        return null;
      }
      if (stream.match('.')) {
        if (state.seenFirstDot) {
          state.seenMoreDots = true;
          // We allow multiple dots. This is to allow for the user to type `{{value.data.someProps}}`
          return 'operator';
        }
        state.seenFirstDot = true;
        if (
          state.seenVariableName &&
          !api.FlowDefinition.blockFieldNames.some((reference) => stream.match(reference, false))
        ) {
          return 'invalid';
        }
        if (
          state.seenInputsName &&
          !flowParameterNames.some((reference) => stream.match(reference, false))
        ) {
          return 'invalid';
        }
        return 'operator';
      }

      if (stream.eatWhile(/\w/)) {
        const word = stream.current();
        state.wholeWords.push(word);
        const fullWord = state.wholeWords.join('.');
        if (additionalAutoCompleteLabels?.includes(fullWord)) {
          state.seenAutocompleteVariable = true;
          return 'moduleKeyword';
        }
        if ((state.seenValidVariableReference || state.seenInputParameter) && state.seenMoreDots) {
          // We allow multiple dots. This is to allow for the user to type `{{value.data.someProps}}`
          return 'propertyName';
        }
        if (state.seenFirstDot) {
          if (state.seenVariableName || state.seenAutocompleteVariable) {
            state.seenVariableName = false;
            state.seenValidVariableReference = api.FlowDefinition.blockFieldNames.includes(word);
            return state.seenValidVariableReference ? 'attributeName' : 'invalid';
          }
          if (state.seenInputsName) {
            state.seenInputsName = false;
            state.seenInputParameter = flowParameterNames.includes(word);
            return state.seenInputParameter ? 'attributeName' : 'invalid';
          }
        }
        if (isJavascriptAllowed && api.FlowDefinition.javascriptKeywords.includes(word)) {
          state.seenVariableName = false;
          state.seenInputsName = false;
          return 'keyword';
        }
        if (isJavascriptAllowed && !Number.isNaN(parseFloat(word))) {
          return 'number';
        }
        if (allowedVariableNames.includes(word)) {
          state.seenVariableName = true;
          return 'variableName';
        }
        if (word === api.FlowDefinition.FlowKeywords.inputs) {
          state.seenInputsName = true;
          return 'variableName';
        }
        return isJavascriptAllowed ? null : 'invalid';
      }
      stream.next();
      return null;
    },
  };
}

interface NodeNameInBracesTokenizerState extends NodeNameTokenizerState {
  singleBraceCount: number;
  inDoubleBraces: boolean;
}

export function getNodeNameInBracesTokenizer(
  props: NodeNameTokenizerProps
): StreamParser<NodeNameInBracesTokenizerState> {
  const { allowedVariableNames, flowParameterNames, additionalAutoCompleteLabels } = props;
  const getStartState = (): NodeNameInBracesTokenizerState => {
    return {
      singleBraceCount: 0,
      inDoubleBraces: false,
      seenFirstDot: false,
      seenMoreDots: false,
      seenVariableName: false,
      seenValidVariableReference: false,
      seenInputsName: false,
      seenInputParameter: false,
      seenAutocompleteVariable: false,
      isInBlockComment: false,
      wholeWords: [],
    };
  };

  return {
    startState: getStartState,
    token(stream, state): TagTypes | null {
      if (stream.eatSpace()) {
        return null;
      }
      if (stream.match('}}')) {
        if (state.singleBraceCount === 0 && !state.inDoubleBraces) {
          return 'invalid';
        }
        if (
          state.inDoubleBraces &&
          !state.seenValidVariableReference &&
          !state.seenInputParameter &&
          !state.seenAutocompleteVariable
        ) {
          if (
            // Allow `{{Start}}` but not `{{Start.}}`
            !(state.seenVariableName || state.seenInputsName) ||
            (state.seenFirstDot && !state.seenAutocompleteVariable)
          ) {
            Object.assign(state, getStartState());
            return 'invalid';
          }
        }
        if (state.singleBraceCount === 0) {
          Object.assign(state, getStartState());
          return 'brace.special';
        }
        if (state.singleBraceCount > 0) {
          const newSingleBraceCount = state.singleBraceCount - 2;
          Object.assign(state, getStartState());
          state.singleBraceCount = newSingleBraceCount;
          return 'brace.special';
        }
      }
      if (stream.match('}')) {
        // A lone `}` cannot close a `{{` — it requires `}}`. Reset state so the
        // rest of the input resumes normal scanning, but preserve any outer
        // brace nesting (the `{{` contributed +2) so the matching outer `}`
        // still tokenizes correctly, mirroring the `}}` handling above.
        if (state.inDoubleBraces) {
          const newSingleBraceCount = state.singleBraceCount - 2;
          Object.assign(state, getStartState());
          state.singleBraceCount = newSingleBraceCount;
          return 'invalid';
        }
        if (state.singleBraceCount > 0) {
          state.singleBraceCount -= 1;
          return 'brace';
        }
        return 'invalid';
      }
      if (state.inDoubleBraces) {
        if (stream.match('{')) {
          return 'invalid';
        }
      }
      if (!state.inDoubleBraces) {
        if (stream.match('{{')) {
          state.singleBraceCount += 2;
          state.inDoubleBraces = true;
          state.seenValidVariableReference = false;
          state.seenInputParameter = false;
          state.seenAutocompleteVariable = false;
          state.wholeWords = [];
          return 'brace.special';
        }
        if (stream.match('{')) {
          state.singleBraceCount += 1;
          return 'brace';
        }
        // If we are not in braces, we are not tokenizing. This allows us to ignore prompt content
        stream.next();
        return null;
      }
      return getNodeNameTokenizer({
        allowedVariableNames,
        flowParameterNames,
        isJavascriptAllowed: false,
        additionalAutoCompleteLabels,
      }).token(stream, state) as TagTypes | null;
    },
  };
}

export function getParsedTokens<T>(language: StreamLanguage<T>, value: string) {
  const parsedTokens: string[] = [];
  language.parser.parse(value).iterate({
    enter: (type) => {
      parsedTokens.push(type.name);
    },
  });
  return parsedTokens;
}

export function isParsedFieldValid<T>(language: StreamLanguage<T>, fieldValue: unknown) {
  if (!isDefined(fieldValue) || typeof fieldValue !== 'string') {
    return true;
  }
  return !getParsedTokens(language, fieldValue).includes('invalid');
}
